import React, { useContext, useEffect, useState } from 'react';
import { RouteComponentProps } from '@reach/router';
import axios from 'axios';
import { CarrierContext, CategoryContext } from '../utils/context';
import { Trailer } from '../types';
import Table, { TableRow, TableHeader, TableDataCell } from '../components/ui/Table';
import InputWithButton from '../components/ui/InputWithButton';
import ComboBox from '../components/ui/ComboBox';
import Toggle from '../components/ui/Toggle';
import Button from '../components/ui/Button';
import { alphabetically } from '../utils/sort';
import Container from '../components/ui/Container';
import * as XLSX from 'xlsx'
import TempModal from '../components/Modals/Error';
import { DateFormatter } from '../commonHelper/DateFormatter';



type Props = RouteComponentProps;

const screenHeight = {
	height: 'calc(100vh - 8.75rem)',
};

async function getAll() {
	try {
		const res = await axios.get(
			`${process.env.REACT_APP_API_URL}search?page=1&limit=25`,
		);
		return res.data;
	} catch (e) {
		alert(e);
	}
}

const sortBy = ['Created At', 'Trailer Number', 'Trailer Location', 'Spot', 'Carrier', 'Category'];
const limitNumber = [5, 10, 15, 25];

async function search(
	page: number,
	limit: number,
	trailerNumber: string,
	carrier: string | undefined,
	category: string | undefined,
	comments: string,
	departed: boolean,
) {
	try {
		let query = new URLSearchParams();

		query.append('page', page.toString());
		query.append('limit', limit.toString());
		query.append('departed', departed.toString());

		if (trailerNumber !== '') {
			query.append('trailerNumber', trailerNumber);
		}

		if (carrier !== undefined) {
			query.append('carrier', carrier);
		}

		if (category !== undefined) {
			query.append('category', category);
		}
		if (comments !== '') {
			query.append('comments', comments.trim());
		}
		var url = `${process.env.REACT_APP_API_URL}search?` + query.toString();

		const res = await axios.get(url);
		return res.data;
	} catch (error) {
		alert(error);
	}
}

export default function Search({ path }: Props) {
	const [data, setData] = useState<Trailer[]>([]);
	const [page, setPage] = useState(1);
	const [carrierOptions, setCarrierOptions] = useState<string[]>([]);
	const [categoriesOptions, setCategoriesOptions] = useState<string[]>([]);
	const [trailerNumber, setTrailerNumber] = useState('');
	const [category, setCategory] = useState<string | undefined>();
	const [comments, setComment] = useState('');
	const [carrier, setCarrier] = useState<string | undefined>();
	const [departed, setDeparted] = useState(false);
	const [sort, setSort] = useState(sortBy[0]);
	const [limit, setLimit] = useState(5);
	const [exportHandler, setexportHandler] = useState(false);
	const [tempModal, setTempModal] = useState(false);
	const [errorMessage, seterrorMessage] = useState('');

	const carriers = useContext(CarrierContext);
	const categories = useContext(CategoryContext);

	useEffect(() => {
		const temp = carriers.map((carrier: any) => {
			return carrier.carrierName;
		});
		setCarrierOptions(temp.sort(alphabetically(true)));
	}, [carriers]);

	useEffect(() => {
		const temp = categories.map((category: any) => {
			return category.categoryName;
		});

		setCategoriesOptions(temp.sort());
	}, [categories]);

	useEffect(() => {
		const fetchData = async () => {
			const results = await getAll();
			setData(results);
		};

		fetchData();
	}, []);

	useEffect(() => {
		handleSubmit();
	}, [page, limit]);

	const handleTrailerNumberSearch = async (value: any) => {
		setTrailerNumber(value);
		setexportHandler(true);
	};

	const handleCommentSearch = async (value: any) => {
		setComment(value);
		setexportHandler(true);
	};

	const handleSubmit = async () => {
		const res = await search(page, limit, trailerNumber, carrier, category, comments, departed);

		setData(res);
		setexportHandler(false);
	};

	const handleOnExport = async () => {
		let data: any[] = await search(0, 0, trailerNumber, carrier, category, comments, departed);
		if (data.length > 0) {
			data.map(item => { item.createdAt = DateFormatter(item.createdAt) })
			const worksheet = XLSX.utils.json_to_sheet(data);
			const headers = [['CREATED AT', 'TRAILER NUMBER', 'TRAILER LOCATION', 'SPOT', 'CARRIER', 'CATEGORY', 'COMMENTS']];
			const workbook = XLSX.utils.book_new();
			XLSX.utils.sheet_add_aoa(worksheet, headers, { origin: "A1" })
			XLSX.utils.book_append_sheet(workbook, worksheet, "Trailer Details");
			XLSX.writeFile(workbook, "TrailerDetails.xlsx");
		}

		else {

			setTempModal(true);
			seterrorMessage('No data available to export');
		}
	}

	const handleClear = async () => {
		const results = await getAll();
		setData(results);
	};

	return (
		<Container>
			<div style={screenHeight} className="h-screen">
				<form
					onSubmit={(e) => {
						e.preventDefault();
						handleSubmit();
					}}
				>
					<div className="flex">
						<div className="mt-1 w-1/5">
							<InputWithButton
								labelText="Search"
								value={trailerNumber}
								onChange={(e) => handleTrailerNumberSearch(e.target.value)}
							/>
						</div>
						<div className="mx-1 mt-2">
							<ComboBox
								labelName={'Category'}
								options={categoriesOptions}
								defaultValue={'Category'}
								value={category}
								valueChange={(value) => {
									setCategory(value);
									setexportHandler(true);
								}}
							/>
						</div>
						<div className="mx-1 mt-2">
							<ComboBox
								labelName={'Carrier'}
								options={carrierOptions}
								defaultValue={'Carrier'}
								value={carrier}
								valueChange={(value) => {
									setCarrier(value);
									setexportHandler(true);
								}}
							/>
						</div>
						<div className="mt-1 w-1/5">
							<InputWithButton
								labelText="Comments"
								placeholder='Comments'
								value={comments}
								onChange={(e) => handleCommentSearch(e.target.value)}
							/>
						</div>
						<div className="ml-2 mt-10">
							<Toggle
								enabled={departed}
								setEnabled={() => {
									setDeparted(!departed);
									setexportHandler(true);
								}}
								label="Departed"
							/>
						</div>
						<div className="ml-2 mt-7">
							<Button
								variant="danger"
								onClick={() => {
									const carrierDropDown = document.getElementById('Carrier') as HTMLSelectElement;
									const categoryDropDown = document.getElementById('Category') as HTMLSelectElement;

									if (carrierDropDown) {
										carrierDropDown.selectedIndex = 0;
									}

									if (categoryDropDown) {
										categoryDropDown.selectedIndex = 0;
									}

									setCarrier(undefined);
									setCategory(undefined);
									setTrailerNumber('');
									setDeparted(false);
									setComment('');
									setexportHandler(false);
									handleClear();
								}}
							>
								Clear
							</Button>
						</div>
					</div>
				</form>
				<div className="flex justify-end" style={{ marginTop: "10px" }}>
					<div className="mr-2 mb-2">
						<Button onClick={handleOnExport} disabled={exportHandler}
							variant="primary">
							Export
						</Button>
						{tempModal ? (
							<TempModal
								open={tempModal}
								close={() => setTempModal(false)}
								warning={true}
								error={errorMessage}
							/>
						) : null}
					</div>

					<span className="mt-1 text-lg">Limit:</span>
					<div className="w-1/8 mx-2">
						<ComboBox
							options={limitNumber}
							value={limit}
							valueChange={(value) => {
								setLimit(value);
							}}
						/>
					</div>
					{/* <span className="mt-1 text-lg">Sort By:</span>
				<div className="w-1/8 ml-2">
					<ComboBox
						options={sortBy}
						value={sort}
						valueChange={(value) => {
							setSort(value);
						}}
					/>
				</div> */}
				</div>

				{data.length > 0 ? (
					<div className="overflow-y-auto h-4/5 my-auto rounded-md">
						<Table
							header={
								<>
									<TableHeader label="Created At" />
									<TableHeader label="Trailer Number" />
									<TableHeader label="Trailer Location" />
									<TableHeader label="Spot" />
									<TableHeader label="Carrier" />
									<TableHeader label="Category" />
									<TableHeader label="Comments" />
								</>
							}
						>
							{data.map((trailer) => {
								return (
									<TableRow>
										<TableDataCell>{DateFormatter(trailer.createdAt)}</TableDataCell>
										<TableDataCell>{trailer.trailerNumber}</TableDataCell>
										<TableDataCell>{trailer.trailerLocation}</TableDataCell>
										<TableDataCell>{trailer.spotNumber}</TableDataCell>
										<TableDataCell>{trailer.carrier}</TableDataCell>
										<TableDataCell>{trailer.category}</TableDataCell>
										<TableDataCell>{trailer.comments}</TableDataCell>
									</TableRow>
								);
							})}
						</Table>
					</div>
				) : (
					<div>No Results Found</div>
				)}

				<div className="flex justify-center w-full">
					{page > 1 ? (
						<div className="w-1/5 mt-2 flex justify-between">
							<Button
								onClick={() => {
									setPage(page - 1);
								}}
							>
								Prev Page
							</Button>
							<Button
								onClick={() => {
									setPage(page + 1);
								}}
							>
								Next Page
							</Button>
						</div>
					) : (
						<div className="mt-2 w-1/5 flex justify-end">
							<Button
								onClick={() => {
									setPage(page + 1);
								}}
							>
								Next Page
							</Button>
						</div>
					)}
				</div>
			</div>
		</Container>
	);
}
